import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, ArrowLeft, MapPin, Clock, Calendar, Users, ArrowRight, FileText, Download, Crown, Lock, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { calculateCompatibility, generateCompatibilityReport, getReportUsage, createCheckout, purchaseAdditionalReport } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { trackEvent } from "@/lib/useAnalytics";
import { toast } from "sonner";
import { motion } from "framer-motion";

// PersonForm extracted outside to prevent re-creation on parent re-render
function PersonForm({ prefix, label, form, updateField }) {
  return (
    <div className="space-y-5">
      <p className="text-white/50 text-xs tracking-[0.2em] uppercase mb-3">{label}</p>
      <div>
        <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Name</Label>
        <Input
          value={form[`${prefix}_name`]}
          onChange={(e) => updateField(`${prefix}_name`, e.target.value)}
          placeholder="Full name"
          className="bg-transparent border-0 border-b border-white/20 rounded-none text-white/90 placeholder:text-white/15 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
          data-testid={`compat-${prefix}-name`}
        />
      </div>
      <div>
        <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth Date</Label>
        <div className="relative">
          <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <Input
            type="date"
            value={form[`${prefix}_birth_date`]}
            onChange={(e) => updateField(`${prefix}_birth_date`, e.target.value)}
            className="bg-transparent border-0 border-b border-white/20 rounded-none pl-6 text-white/90 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
            data-testid={`compat-${prefix}-date`}
          />
        </div>
      </div>
      <div>
        <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth Time</Label>
        <div className="relative">
          <Clock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
          <Input
            type="time"
            value={form[`${prefix}_birth_time`]}
            onChange={(e) => updateField(`${prefix}_birth_time`, e.target.value)}
            className="bg-transparent border-0 border-b border-white/20 rounded-none pl-6 text-white/90 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
            data-testid={`compat-${prefix}-time`}
          />
        </div>
      </div>
      <div>
        <Label className="text-white/40 text-xs tracking-[0.15em] uppercase">Birth City</Label>
        <div className="relative">
          <MapPin className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none z-10" />
          <CityAutocomplete
            value={form[`${prefix}_birth_city`]}
            onChange={(val) => updateField(`${prefix}_birth_city`, val)}
            placeholder="e.g. Mumbai, London"
            className="bg-transparent border-0 border-b border-white/20 rounded-none pl-6 text-white/90 placeholder:text-white/15 focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
            testId={`compat-${prefix}-city`}
          />
        </div>
      </div>
    </div>
  );
}

export default function Compatibility() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    person1_name: "", person1_birth_date: "", person1_birth_time: "", person1_birth_city: "",
    person2_name: "", person2_birth_date: "", person2_birth_time: "", person2_birth_city: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const navigate = useNavigate();

  const updateField = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  // Fetch usage on mount
  useEffect(() => {
    if (user) {
      getReportUsage().then(setUsage).catch(console.error);
    }
  }, [user]);

  const isPremium = usage?.is_premium;
  const canGenerateFree = usage?.can_generate_free;
  const totalRemaining = usage?.total_remaining || 0;
  const additionalCredits = usage?.additional_credits || 0;
  const remainingIncluded = usage?.remaining_included || 0;

  const handlePurchaseAdditional = async () => {
    trackEvent("checkout_started", { report: "additional_report", type: "compatibility" });
    try {
      const checkout = await purchaseAdditionalReport("compatibility");
      if (checkout.url) window.location.href = checkout.url;
    } catch {
      toast.error("Failed to start checkout");
    }
  };

  const handleCalculate = async () => {
    // Validate all fields first
    const required = Object.entries(form);
    for (const [key, val] of required) {
      if (!val.trim()) {
        toast.error("Please fill in all fields for both people");
        return;
      }
    }

    // Check access
    if (!isPremium) {
      toast.error("Compatibility analysis requires Premium subscription or single purchase.");
      return;
    }

    if (!canGenerateFree) {
      toast.error(`No reports remaining. Purchase additional at $1.00 each.`);
      return;
    }

    try {
      setLoading(true);
      trackEvent("compatibility_run");
      const data = await calculateCompatibility(form);
      setResult(data);
      
      // Refresh usage after generation
      getReportUsage().then(setUsage).catch(console.error);
    } catch (e) {
      if (e.response?.status === 402) {
        const detail = e.response?.data?.detail;
        toast.error(detail?.message || "Payment required for this report");
        // Refresh usage to get accurate state
        getReportUsage().then(setUsage).catch(console.error);
      } else {
        toast.error("Calculation failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    trackEvent("checkout_started", { report: "compatibility" });
    try {
      const checkout = await createCheckout({
        package_id: "compatibility",
        origin_url: window.location.origin,
        metadata: form,
      });
      if (checkout.url) window.location.href = checkout.url;
    } catch {
      toast.error("Failed to start checkout");
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="compat-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Back</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-3">
            <Users className="w-6 h-6 text-white/40" />
            <h1 className="text-4xl sm:text-5xl font-light text-white/95" data-testid="compat-title">Compatibility</h1>
          </div>
          <p className="text-white/40 text-sm mb-4">
            Nakshatra synastry — explore the emotional resonance between two lunar placements.
          </p>
          
          {/* Access indicator */}
          <div className="flex items-center gap-2 mb-10">
            {isPremium ? (
              <>
                <Crown className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm" data-testid="premium-indicator">
                  Premium — {remainingIncluded} included{additionalCredits > 0 ? ` + ${additionalCredits} purchased` : ""} remaining
                </span>
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 text-white/40" />
                <span className="text-white/40 text-sm" data-testid="locked-indicator">
                  Premium or single purchase required
                </span>
              </>
            )}
          </div>
        </motion.div>

        {!result ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="rounded-lg p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <PersonForm prefix="person1" label="Person 1" form={form} updateField={updateField} />
              </div>
              <div className="rounded-lg p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <PersonForm prefix="person2" label="Person 2" form={form} updateField={updateField} />
              </div>
            </div>

            {isPremium ? (
              canGenerateFree ? (
                <Button
                  onClick={handleCalculate}
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-white/90 rounded-full h-12 text-sm tracking-wider font-medium"
                  data-testid="compat-calc-btn"
                >
                  {loading ? "Calculating..." : "Calculate Compatibility"}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button
                    onClick={handlePurchaseAdditional}
                    className="w-full bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full h-12 text-sm tracking-wider font-medium"
                    data-testid="compat-purchase-additional-btn"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Purchase Additional Report — $1.00
                  </Button>
                  <p className="text-white/30 text-xs text-center">Monthly allowance used. Resets {usage?.reset_date}</p>
                </div>
              )
            ) : (
              <div className="space-y-3">
                <Button
                  onClick={handlePurchase}
                  className="w-full bg-white text-black hover:bg-white/90 rounded-full h-12 text-sm tracking-wider font-medium"
                  data-testid="compat-purchase-btn"
                >
                  Purchase Compatibility — $3.99
                </Button>
                <Link to="/pricing" className="block">
                  <Button
                    variant="outline"
                    className="w-full border-amber-500/20 text-amber-400 hover:bg-amber-500/10 rounded-full h-12 text-sm"
                    data-testid="compat-subscribe-btn"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Subscribe to Premium — $4.29/mo
                  </Button>
                </Link>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Results */}
            <div className="space-y-6" data-testid="compat-results">
              {/* Score Header */}
              <div className="rounded-lg p-6 sm:p-8 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <p className="text-white/30 text-xs tracking-[0.2em] uppercase mb-4">Compatibility Score</p>
                <div className="text-6xl font-light text-white/95" data-testid="compat-score">
                  {result.percentage}%
                </div>
              </div>

              {/* Both placements */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[result.person1, result.person2].map((p, i) => (
                  <div key={i} className="rounded-lg p-5" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-2">{p.name}</p>
                    <p className="text-white/90 text-xl font-light mb-1">{p.nakshatra_name}</p>
                    <p className="text-white/50 text-sm">Pada {p.pada} — {p.degree_in_sign}&deg; {p.sidereal_sign}</p>
                    <p className="text-white/30 text-xs mt-1">Ruler: {p.ruler}</p>
                  </div>
                ))}
              </div>

              {/* Score Breakdown */}
              <div className="rounded-lg p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-4">Score Breakdown</p>
                <div className="space-y-3">
                  {Object.entries(result.scores).map(([key, val]) => {
                    const maxMap = { nadi: 8, rashi: 6, gana: 6, bhakut: 7, nakshatra_lord: 5 };
                    const max = maxMap[key] || 8;
                    const pct = (val / max) * 100;
                    return (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-white/60 capitalize">{key.replace("_", " ")}</span>
                          <span className="text-white/40">{val}/{max}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5">
                          <div className="h-full rounded-full bg-white/30 transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* AI Analysis */}
              {result.analysis && (
                <div className="rounded-lg p-6 sm:p-8" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="compat-analysis">
                  <p className="text-white/40 text-xs tracking-[0.15em] uppercase mb-4">Analysis</p>
                  <div className="prose-dark text-white/60 text-sm leading-relaxed whitespace-pre-line">
                    {result.analysis.replace(/\*\*/g, "")}
                  </div>
                </div>
              )}

              {/* Compatibility Report PDF */}
              {isPremium && (
                <div
                  className="rounded-lg p-6 sm:p-8"
                  style={{ background: "rgba(251,191,36,0.05)", border: "1px solid rgba(251,191,36,0.2)" }}
                  data-testid="compat-report-section"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <FileText className="w-4 h-4 text-amber-400" />
                    <p className="text-amber-400 text-xs tracking-[0.15em] uppercase">Full Compatibility Report</p>
                  </div>
                  <p className="text-white/70 text-sm mb-4">
                    Get a comprehensive PDF with emotional chemistry, communication dynamics, intimacy patterns, growth areas, and personalized guidance.
                  </p>
                  <Button
                    onClick={async () => {
                      toast.info("Generating compatibility report...");
                      try {
                        const blob = await generateCompatibilityReport(form);
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `compatibility_report.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success("Report downloaded!");
                        // Refresh usage
                        getReportUsage().then(setUsage).catch(console.error);
                      } catch (e) {
                        if (e.response?.status === 402) {
                          toast.error("Additional payment required");
                        } else {
                          toast.error("Report generation failed");
                        }
                      }
                    }}
                    className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full px-6 h-10 text-sm gap-2"
                    data-testid="download-compat-report-btn"
                  >
                    <Download className="w-4 h-4" />
                    Download Full Report
                  </Button>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => { 
                    setResult(null); 
                    setForm({ person1_name: "", person1_birth_date: "", person1_birth_time: "", person1_birth_city: "", person2_name: "", person2_birth_date: "", person2_birth_time: "", person2_birth_city: "" }); 
                  }}
                  className="bg-white/10 text-white/80 hover:bg-white/15 border border-white/10 rounded-full px-6 h-11 text-sm"
                  data-testid="compat-new-btn"
                >
                  New Reading
                </Button>
                <Button
                  variant="ghost"
                  className="text-white/50 hover:text-white text-sm"
                  onClick={() => navigate(`/nakshatras/${result.person1.nakshatra_slug}`)}
                >
                  Explore {result.person1.nakshatra_name}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
